import { cx } from "@suse-o11y/class-variance";
import { onTimeout } from "@suse-o11y/on-timeout";
import { WindowedPriorityQueue } from "@suse-o11y/windowed-priority-queue";
import { notification as antdNotification } from "antd/es";
import type { ArgsProps, NotificationInstance } from "antd/es/notification";
import { nanoid } from "nanoid";
import React from "react";

import { NotificationContext } from "./_context";
import styles from "./_notification.module.css";

let api: undefined | NotificationInstance;

export function setNotificationInstance(notificationApi: NotificationInstance) {
	api = notificationApi;
}

export type NotificationPlacement = "topLeft" | "topRight" | "bottomLeft" | "bottomRight";
export type IconType = "success" | "info" | "error" | "warning";

export type NotificationArgs = ArgsProps & { priority?: number };

type ArgsPropsWithKey = NotificationArgs & { key: string };

const MAX_COUNT = 3;

function comparePriority(a: ArgsPropsWithKey, b: ArgsPropsWithKey): number {
	if (a.priority !== undefined && b.priority !== undefined) {
		return b.priority - a.priority;
	}
	if (a.priority !== undefined) {
		return -1;
	}
	if (b.priority !== undefined) {
		return 1;
	}
	return 0;
}

export class Notification {
	private readonly queue: WindowedPriorityQueue<ArgsPropsWithKey>;

	public constructor(queue?: WindowedPriorityQueue<ArgsPropsWithKey>) {
		this.queue = queue ?? new WindowedPriorityQueue<ArgsPropsWithKey>(MAX_COUNT, comparePriority);

		antdNotification.config({
			placement: "topRight",
			top: 56,
		});
	}

	private show(method: ArgsProps["type"], args: NotificationArgs) {
		const key = args.key ?? nanoid();

		const composedArgs = {
			...args,
			key,
			type: method,
			className: cx(styles.notification, args.className),
			onClose: () => this.close(key),
			message: (
				<NotificationContext.Provider value={() => this.close(key)}>{args.message}</NotificationContext.Provider>
			),
		} satisfies NotificationArgs;

		if (this.queue.has(key)) {
			return key;
		}

		this.queue.add(composedArgs);

		if (this.queue.isActive(key)) {
			api?.open(composedArgs);
		}

		return key;
	}

	private onCloseNotification(key: string) {
		const shouldOpen = this.queue.release(key);

		if (shouldOpen != null) {
			api?.open(shouldOpen);
		}
	}

	public success(args: NotificationArgs) {
		return this.show("success", args);
	}

	public error(args: NotificationArgs) {
		return this.show("error", args);
	}

	public info(args: NotificationArgs) {
		return this.show("info", args);
	}

	public warning(args: NotificationArgs) {
		return this.show("warning", args);
	}

	public open(args: NotificationArgs) {
		return this.show(undefined, args);
	}

	public close(key: string) {
		this.onCloseNotification(key);
		antdNotification.close(key);
	}

	public destroy() {
		this.queue.clear();

		return antdNotification.destroy();
	}
}
export const notification = new Notification();

export type CancelNotification = () => void;
export function delayNotification(
	notificationCb: () => void,
	notificationKey: string,
	delayTime: number,
): CancelNotification {
	const cleanup = onTimeout(() => {
		notificationCb();
	}, delayTime);

	return () => {
		cleanup();
		notification.close(notificationKey);
	};
}

export const DONT_AUTO_DISMISS = 0;
